import type {
  RawThread, RawMessage, Thread, ThreadMessage, PendingMessage, ThreadsResponse, ThreadMessagesResponse, PendingMessagesResponse
} from './QueryTypes';

function normalizeStatus(raw?: string): 'PENDING' | 'APPROVED' | 'REJECTED' | undefined {
  if (!raw) return undefined;
  const s = raw.toUpperCase();
  if (s === 'PENDING' || s === 'PENDING_APPROVAL') return 'PENDING';
  if (s === 'APPROVED') return 'APPROVED';
  if (s === 'REJECTED') return 'REJECTED';
  return undefined;
}

function toThread(raw: RawThread): Thread {
  const patientPkRaw = raw.PatientPK ?? raw.ThreadPK ?? '';
  const derivedPatientId = patientPkRaw.replace(/^PATIENT#/, '');
  const patientName = raw.patient_name ?? raw.PatientName ?? '';

  return {
    thread_id: raw.thread_id ?? raw.ThreadId ?? '',
    patient_id: raw.patient_id ?? derivedPatientId,
    patient_name: patientName,
    subject: raw.subject ?? raw.Subject ?? '',
    created_at: raw.created_at ?? raw.CreatedAt ?? raw.Date ?? '',
    updated_at: raw.updated_at ?? raw.LastMessageAt ?? raw.UpdatedAt ?? raw.CreatedAt ?? raw.Date ?? '',
    status: raw.status ?? raw.Status,
    initiated_by_name: raw.InitiatedByName,
    initiated_by: raw.InitiatedBy,
    doctor_pk: raw.DoctorPK,
    participant_pks: raw.ParticipantPKs,
    thread_type: raw.ThreadType,
  };
}

function toMessage(raw: RawMessage): ThreadMessage {
  const status = normalizeStatus(raw.status ?? raw.Status);

  const message: ThreadMessage = {
    message_id: raw.message_id ?? raw.MessageId ?? '',
    sender_name: raw.sender_name ?? raw.SenderName ?? '',
    sender_role: raw.sender_role ?? raw.SenderRole ?? '',
    sender_pk: raw.SenderPK ?? raw.sender_pk,
    text: raw.text ?? raw.Text ?? '',
    created_at: raw.created_at ?? raw.CreatedAt ?? raw.Date ?? '',
  };

  if (status) message.status = status;
  if (raw.rejection_reason ?? raw.RejectionReason) message.rejection_reason = raw.rejection_reason ?? raw.RejectionReason ?? undefined;
  if (raw.approved_at ?? raw.ApprovedAt) message.approved_at = raw.approved_at ?? raw.ApprovedAt ?? undefined;

  return message;
}

function toPendingMessage(raw: RawMessage & RawThread): PendingMessage {
  const patientPkRaw = raw.PatientPK ?? raw.ThreadPK ?? '';
  const patient_id = raw.patient_id ?? patientPkRaw.replace(/^PATIENT#/, '');
  const status = normalizeStatus(raw.status ?? raw.Status) ?? 'PENDING';

  return {
    ...toMessage(raw),
    thread_id: raw.thread_id ?? raw.ThreadId ?? '',
    patient_id,
    status,
    patient_pk: raw.PatientPK,
    sender_pk: raw.SenderPK ?? raw.sender_pk,
  };
}

export function transformThreadsResponse(data: any): ThreadsResponse {
  const rawList: RawThread[] = Array.isArray(data?.threads) ? data.threads : [];
  const threads = rawList.map(toThread);
  return { threads, count: threads.length };
}

export function transformThreadMessagesResponse(data: any): ThreadMessagesResponse {
  const rawList: RawMessage[] = Array.isArray(data?.messages) ? data.messages : [];
  const messages = rawList.map(toMessage);
  return { messages, count: messages.length };
}

export function transformPendingMessagesResponse(data: any): PendingMessagesResponse {
  const rawList: any[] = data?.pending_messages ?? data?.messages ?? [];
  const pending_messages = rawList.map(toPendingMessage);
  return { pending_messages, count: pending_messages.length };
}
